import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useCallback,
  useContext,
} from "react";
import Select from "react-select";
import { IMaskInput } from "react-imask";
import "./UpdateUser.css";
import Preloader from "../../../Preloader/Preloader";
import EmployeePhoto from "../../../../images/cat-photo.png";
import {
  getUserById,
  getUsersSummary,
  updateUser,
  updateUserImage,
  getAllGroups,
  getAllRoles,
  getAllCompanies,
  getAllAccessLevels,
  getAllWorkFormats,
  getAllSections,
  getLatestValueFromHistory,
  getSalaryDateFromHistory,
} from "../../../../api/api";
import {
  buildUserUpdatePayload,
  cleanUserUpdatePayload,
  normalizeIsFired,
} from "../../../../utils/buildUserUpdatePayload";
import {
  applyPendingEffectiveDates,
  mergeSentEffectiveDates,
  resolveCompanyEffectiveDateForForm,
  resolveDepartmentEffectiveDateForForm,
  resolveSalaryEffectiveDate,
  resolveFiredDate,
  resolveWorkHoursPerDayEffectiveDate,
  savePendingEffectiveDates,
} from "../../../../utils/resolveUserEffectiveDates";
import FinansistContragentSelect from "../Register/FinansistContragentSelect";
import {
  buildFinansistContragentMappingPayload,
  contragentMappingToFormState,
  emptyFinansistContragentFormState,
  formatMatchedFio,
  getUserFinansistContragentMapping,
  isFinansistContragentMappingChanged,
} from "../../../../utils/finansistContragentMapping";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../../contexts/CurrentUserContext";

const EMPTY_POSITION = {
  company: null,
  department: null,
  group: null,
  role: null,
  grade: "",
  section: "",
  teamLeader: null,
  companyEffectiveDate: "",
  departmentEffectiveDate: "",
  groupEffectiveDate: "",
  roleEffectiveDate: "",
  sectionEffectiveDate: "",
  teamLeaderEffectiveDate: "",
};

// Хук для обработки кликов вне компонента
const useClickOutside = (handler) => {
  const ref = useRef();

  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [handler]);

  return ref;
};

// Компонент календаря
const Calendar = forwardRef(({ value, onChange, maxDate }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(
    value ? new Date(value) : new Date(),
  );
  const calendarRef = useClickOutside(() => setIsOpen(false));

  const months = [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
  ];

  const years = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i,
  );

  const handleDateSelect = (date) => {
    if (maxDate && date > maxDate) return;
    // Создаем дату в UTC, чтобы избежать проблем с часовым поясом
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0));
    onChange(utcDate);
    setIsOpen(false);
  };

  const formatDate = (date) => {
    if (!date) return "";
    // Преобразуем UTC дату в локальную дату для отображения
    const localDate = new Date(date);
    return localDate.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    // Добавляем пустые ячейки для выравнивания календаря
    for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Добавляем дни месяца
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isDisabled = maxDate && date > maxDate;
      days.push(
        <div
          key={day}
          className={`calendar-day ${isDisabled ? "disabled" : ""}`}
          onClick={() => !isDisabled && handleDateSelect(date)}
        >
          {day}
        </div>,
      );
    }

    return days;
  };

  return (
    <div className="calendar-wrapper" ref={calendarRef}>
      <input
        ref={ref}
        className="registration__input"
        value={formatDate(value)}
        onClick={() => setIsOpen(true)}
        readOnly
        placeholder="Выберите дату"
      />
      {isOpen && (
        <div className="calendar-popup">
          <div className="calendar-header">
            <select
              value={currentDate.getMonth()}
              onChange={(e) =>
                setCurrentDate(
                  new Date(
                    currentDate.getFullYear(),
                    parseInt(e.target.value),
                    1,
                  ),
                )
              }
            >
              {months.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
            <select
              value={currentDate.getFullYear()}
              onChange={(e) =>
                setCurrentDate(
                  new Date(parseInt(e.target.value), currentDate.getMonth(), 1),
                )
              }
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className="calendar-days-header">
            <div>Пн</div>
            <div>Вт</div>
            <div>Ср</div>
            <div>Чт</div>
            <div>Пт</div>
            <div>Сб</div>
            <div>Вс</div>
          </div>
          <div className="calendar-days">{renderCalendar()}</div>
        </div>
      )}
    </div>
  );
});

Calendar.displayName = "Calendar";

// Компонент для маскированного ввода телефона
const InputWithRef = forwardRef((props, ref) => (
  <IMaskInput
    {...props}
    inputRef={ref}
    mask="+7 (000) 000-00-00"
    definitions={{
      "#": /[1-9]/,
    }}
    unmask={false}
    lazy={false}
  />
));

InputWithRef.displayName = "InputWithRef";

// Функция для перевода форматов работы на русский язык
const translateWorkFormat = (formatName) => {
  const translations = {
    FIVE_DAY_8_HOUR: "5-дневная рабочая неделя, 8 часов",
    FIVE_DAY_4_HOUR: "5-дневная рабочая неделя, 4 часа",
    SHIFT_SCHEDULE: "Сменный график",
    INTERN: "Стажировка",
    REMOTE_SCHEDULE: "Удаленный график",
  };
  return translations[formatName] || formatName;
};

// Компонент для отображения истории изменений
const HistoryTooltip = ({ history, fieldName, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const buttonRef = useRef(null);
  const timeoutRef = useRef(null);

  // Очищаем таймер при размонтировании компонента
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "Не указана";
    const date = new Date(dateString);
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatValue = (value, fieldName) => {
    if (value === null || value === undefined) return "Не указано";

    switch (fieldName) {
      case "salary_amount":
        return `${value} тенге`;
      case "is_fired":
      case "status":
        return value ? "Уволен" : "Не уволен";
      case "work_start_time":
        return value.substring(0, 5); // Показываем только часы:минуты
      case "work_hours_per_day":
        return `${value} часов`;
      case "work_format":
        return translateWorkFormat(value?.name || value);
      case "group":
        return value?.name || value;
      case "primary_role":
        return value?.name || value;
      case "primary_section":
        return value?.name || value;
      case "primary_team_leader":
        return value ? `${value.surname} ${value.name}` : value;
      case "access_level":
        return value?.name || value;
      case "company":
        return value?.name || value;
      default:
        return String(value);
    }
  };

  const handleMouseEnter = (e) => {
    if (!hasHistory) {
      return;
    }

    // Очищаем предыдущий таймер
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Устанавливаем небольшую задержку перед показом
    timeoutRef.current = setTimeout(() => {
      const rect = buttonRef.current.getBoundingClientRect();
      const tooltipWidth = 300; // Примерная ширина тултипа
      const viewportWidth = window.innerWidth;

      // Позиционируем относительно viewport для fixed позиционирования
      let left = rect.left;
      if (left + tooltipWidth > viewportWidth) {
        left = viewportWidth - tooltipWidth - 10; // Отступ от края
      }

      setPosition({
        top: rect.bottom + 8, // Увеличиваем отступ для удобства
        left: left,
      });
      setIsVisible(true);
    }, 300); // 300ms задержка
  };

  const handleMouseLeave = () => {
    // Очищаем таймер при уходе мыши
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Добавляем небольшую задержку перед скрытием тултипа
    // Это позволяет пользователю переместить мышь на тултип
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  // Проверяем наличие истории
  const hasHistory = history && history.length > 0;

  // Сортируем историю по дате (самая поздняя первая)
  const sortedHistory = hasHistory
    ? [...history]
        .filter((item) => item.effective_date !== null)
        .sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date))
    : [];

  return (
    <div className="history-container">
      {children}
      <button
        ref={buttonRef}
        type="button"
        className="history-button"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        title={
          hasHistory
            ? "Показать историю изменений"
            : "История изменений отсутствует"
        }
        style={{ opacity: hasHistory ? 1 : 0.5 }}
      >
        📊
      </button>
      {isVisible && (
        <div
          ref={tooltipRef}
          className="history-tooltip"
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            zIndex: 10000,
          }}
          onMouseEnter={() => {
            // Отменяем скрытие тултипа при наведении на него
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
          }}
          onMouseLeave={() => {
            // При уходе с тултипа сразу скрываем его
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            setIsVisible(false);
          }}
        >
          <div className="history-tooltip-header">История изменений</div>
          <div className="history-tooltip-content">
            {sortedHistory.map((item, index) => (
              <div key={index} className="history-item">
                <div className="history-value">
                  {formatValue(
                    fieldName === "is_fired"
                      ? item.is_fired !== undefined
                        ? item.is_fired
                        : item.status
                      : item[fieldName] || item,
                    fieldName,
                  )}
                </div>
                <div className="history-date">
                  {formatDate(item.effective_date)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const getOrgEffectiveDatesFromUser = (user) => {
  const latestDepartmentEntry = getLatestValueFromHistory(
    user.user_department_history,
    "department",
  );
  const latestGroupEntry = getLatestValueFromHistory(
    user.user_group_history,
    "group",
  );
  const latestRoleEntry = getLatestValueFromHistory(
    user.user_primary_role_history,
    "primary_role",
  );
  const latestSectionEntry = getLatestValueFromHistory(
    user.primary_section_history,
    "primary_section",
  );
  const latestTeamLeaderEntry = getLatestValueFromHistory(
    user.primary_team_leader_history,
    "primary_team_leader",
  );

  return {
    company:
      getLatestValueFromHistory(user.company_history, "company")
        ?.effective_date ||
      user.company_id_effective_date ||
      user.company_effective_date ||
      "",
    department:
      latestDepartmentEntry?.effective_date ||
      user.department_effective_date ||
      "",
    group: latestGroupEntry?.effective_date || user.group_effective_date || "",
    role: latestRoleEntry?.effective_date || user.role_effective_date || "",
    section:
      latestSectionEntry?.effective_date || user.section_effective_date || "",
    teamLeader:
      latestTeamLeaderEntry?.effective_date ||
      user.team_leaders_effective_date ||
      "",
  };
};

const buildPositionsFromUser = (user, companies) => {
  const dates = getOrgEffectiveDatesFromUser(user);
  const newPositions = [];

  const makeDepartmentRef = (department) =>
    department
      ? { value: department.id, label: department.name }
      : user.hr_data?.primary_department
        ? {
            value: user.hr_data.primary_department.id,
            label: user.hr_data.primary_department.name,
          }
        : null;

  if (user.departments?.length > 0) {
    user.departments.forEach((department, deptIndex) => {
      if (department.groups?.length > 0) {
        department.groups.forEach((group, groupIndex) => {
          const positionIndex = deptIndex * 100 + groupIndex;
          newPositions.push({
            id: Date.now() + positionIndex,
            ...EMPTY_POSITION,
            company:
              companies.find((c) => c.value === user.company_id) || null,
            department: makeDepartmentRef(department),
            group: {
              value: group.id,
              label: group.name,
              roles:
                (group.roles || []).filter((role) => role.is_active) || [],
            },
            role:
              group.roles?.length > 0
                ? { value: group.roles[0].id, label: group.roles[0].name }
                : null,
            grade: user.hr_data?.grade || "",
            section:
              group.sections?.length > 0
                ? {
                    value: group.sections[0].id,
                    label: group.sections[0].name,
                  }
                : null,
            teamLeader:
              user.team_leaders?.length > 0
                ? {
                    value: user.team_leaders[0].id,
                    label: `${user.team_leaders[0].surname} ${user.team_leaders[0].name}`,
                  }
                : null,
            companyEffectiveDate: dates.company,
            departmentEffectiveDate: dates.department,
            groupEffectiveDate: dates.group,
            roleEffectiveDate: dates.role,
            sectionEffectiveDate: dates.section,
            teamLeaderEffectiveDate: dates.teamLeader,
          });
        });
      }
    });
  }

  if (user.hr_data) {
    if (newPositions.length > 0) {
      newPositions[0] = {
        ...newPositions[0],
        company:
          companies.find((c) => c.value === user.company_id) ||
          newPositions[0].company,
        department:
          makeDepartmentRef(user.departments?.[0]) ||
          newPositions[0].department,
        group: user.hr_data.primary_group
          ? {
              value: user.hr_data.primary_group.id,
              label: user.hr_data.primary_group.name,
              roles: newPositions[0].group?.roles || [],
            }
          : newPositions[0].group,
        role: user.hr_data.primary_role
          ? {
              value: user.hr_data.primary_role.id,
              label: user.hr_data.primary_role.name,
            }
          : newPositions[0].role,
        section: user.hr_data.primary_section
          ? {
              value: user.hr_data.primary_section.id,
              label: user.hr_data.primary_section.name,
            }
          : newPositions[0].section,
        teamLeader: user.hr_data.primary_team_leader
          ? {
              value: user.hr_data.primary_team_leader.team_leader_id,
              label: `${user.hr_data.primary_team_leader.surname} ${user.hr_data.primary_team_leader.name}`,
            }
          : newPositions[0].teamLeader,
      };
    } else {
      newPositions.push({
        id: Date.now(),
        ...EMPTY_POSITION,
        company: companies.find((c) => c.value === user.company_id) || null,
        department: makeDepartmentRef(null),
        group: user.hr_data.primary_group
          ? {
              value: user.hr_data.primary_group.id,
              label: user.hr_data.primary_group.name,
              roles: [],
            }
          : null,
        role: user.hr_data.primary_role
          ? {
              value: user.hr_data.primary_role.id,
              label: user.hr_data.primary_role.name,
            }
          : null,
        grade: user.hr_data.grade || "",
        section: user.hr_data.primary_section
          ? {
              value: user.hr_data.primary_section.id,
              label: user.hr_data.primary_section.name,
            }
          : null,
        teamLeader: user.hr_data.primary_team_leader
          ? {
              value: user.hr_data.primary_team_leader.team_leader_id,
              label: `${user.hr_data.primary_team_leader.surname} ${user.hr_data.primary_team_leader.name}`,
            }
          : null,
        companyEffectiveDate: dates.company,
        departmentEffectiveDate: dates.department,
        groupEffectiveDate: dates.group,
        roleEffectiveDate: dates.role,
        sectionEffectiveDate: dates.section,
        teamLeaderEffectiveDate: dates.teamLeader,
      });
    }
  }

  if (newPositions.length > 0) {
    return newPositions;
  }

  return [{ id: Date.now(), ...EMPTY_POSITION }];
};

function UpdateUser() {
  const { userData: currentUser } = useContext(UserContext);
  const isAdms = currentUser.access_levels.some(
    (level) => level.name === "АДМС",
  );
  const isHRInspector = currentUser.access_levels.some(
    (level) => level.name === "ИК",
  );
  const isFinanceInspector = currentUser.access_levels.some(
    (level) => level.name === "ФИН",
  );
  // ИК шире ФИН: при обоих уровнях показываем форму ИК, не урезанную ФИН
  const isFinanceOnlyEditor = isFinanceInspector && !isHRInspector;

  // Для АДМС: по умолчанию сокращённая форма (частичное редактирование)
  const [fullEditMode, setFullEditMode] = useState(false);

  const [positions, setPositions] = useState([
    { id: Date.now(), ...EMPTY_POSITION },
  ]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    lastName: "",
    firstName: "",
    middleName: "",
    isFired: false,
    hireDate: "",
    // Даты эффективности
    firedDate: "",
    accessLevel: [],
    accessLevelEffectiveDate: "",
    salary: "",
    salaryEffectiveDate: "",
    image: null,
    imageFile: null,
    birthDate: "",
    phone: "",
    hobbies: "",
    companyId: null,
    companyIdEffectiveDate: "",
    departmentEffectiveDate: "",
    groups: [],
    groupEffectiveDate: "",
    roles: [],
    roleEffectiveDate: "",
    sectionEffectiveDate: "",
    teamLeaders: [],
    teamLeadersEffectiveDate: "",
    primaryTeamLeaderIdEffectiveDate: "",
    primaryGroupIdEffectiveDate: "",
    primarySectionIdEffectiveDate: "",
    primaryRoleIdEffectiveDate: "",
    groupLeaders: [],
    departmentHeads: [],
    // Грейд
    grade: "",
    gradeEffectiveDate: "",
    // График и формат
    workFormat: 1,
    workFormatEffectiveDate: "",
    workHoursPerDay: 8,
    workHoursPerDayEffectiveDate: "",
    workStartTime: "09:00:00.000Z",
    workStartTimeEffectiveDate: "",
  });
  const [groups, setGroups] = useState([]);
  const [inputErrors, setInputErrors] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [imageErrors, setImageErrors] = useState([]);
  const [dateFieldErrors, setDateFieldErrors] = useState({});
  const [formValidationTrigger, setFormValidationTrigger] = useState(0);
  const phoneInputRef = useRef(null);
  const [companies, setCompanies] = useState([]);
  const [workFormats, setWorkFormats] = useState([]);
  const [sections, setSections] = useState([]);
  const [groupUsers, setGroupUsers] = useState([]);
  const navigate = useNavigate();
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [accessLevels, setAccessLevels] = useState([]);
  const [initialFormData, setInitialFormData] = useState({});
  const [finansistContragent, setFinansistContragent] = useState(
    emptyFinansistContragentFormState,
  );

  const getFieldLabel = (field) => {
    const labels = {
      companyEffectiveDate: "Дата вступления в силу (Компания)",
      groupEffectiveDate: "Дата вступления в силу (Отдел)",
      roleEffectiveDate: "Дата вступления в силу (Должность)",
      sectionEffectiveDate: "Дата вступления в силу (Секция)",
      teamLeaderEffectiveDate: "Дата вступления в силу (Руководитель группы)",
    };
    return labels[field] || field;
  };

  const validateDateFields = useCallback((positionsToValidate) => {
    const errors = {};

    positionsToValidate.forEach((position, index) => {
      const requiredFields = [
        "companyEffectiveDate",
        "groupEffectiveDate",
        "roleEffectiveDate",
        "sectionEffectiveDate",
        "teamLeaderEffectiveDate",
      ];

      requiredFields.forEach((field) => {
        const fieldKey = `${index}_${field}`;
        if (!position[field] || position[field].trim() === "") {
          errors[fieldKey] = `Поле "${getFieldLabel(
            field,
          )}" обязательно для заполнения`;
        }
      });
    });

    setDateFieldErrors(errors);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  };

  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /** is_fired из ответа API или из истории увольнений; null — не уволен */
  const resolveIsFiredFromUser = (user) => {
    if (user?.is_fired !== undefined && user?.is_fired !== null) {
      return normalizeIsFired(user.is_fired);
    }
    const latestFired = getLatestValueFromHistory(
      user?.user_status_history,
      "is_fired",
    );
    if (latestFired?.is_fired !== undefined && latestFired?.is_fired !== null) {
      return normalizeIsFired(latestFired.is_fired);
    }
    const latestStatus = getLatestValueFromHistory(
      user?.user_status_history,
      "status",
    );
    if (latestStatus?.status !== undefined && latestStatus?.status !== null) {
      return normalizeIsFired(latestStatus.status);
    }
    return false;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Получаем активных пользователей
        const activeUsersData = await getUsersSummary({
          pageSize: 1000,
          isActive: true,
        });

        // Получаем неактивных пользователей
        const inactiveUsersData = await getUsersSummary({
          pageSize: 1000,
          isActive: false,
        });

        // Объединяем результаты
        const allUsers = [
          ...(activeUsersData?.users || []),
          ...(inactiveUsersData?.users || []),
        ];

        if (allUsers.length > 0) {
          setUsers(allUsers);
        } else {
          console.error("Получены некорректные данные пользователей:", {
            activeUsersData,
            inactiveUsersData,
          });
          setUsers([]);
        }

        const groupsData = await getAllGroups();
        setGroups(
          groupsData.map((group) => ({
            value: group.id,
            label: group.name,
            roles: group.roles || [],
          })),
        );

        try {
          const companiesData = await getAllCompanies();
          setCompanies(
            companiesData.map((company) => ({
              value: company.id,
              label: company.name,
            })),
          );
        } catch (companyError) {
          console.error("Ошибка при загрузке компаний:", companyError);
          // Можно добавить состояние для отображения ошибки пользователю
          setCompanies([]);
        }

        const accessLevelsData = await getAllAccessLevels();
        setAccessLevels(accessLevelsData);

        const workFormatsData = await getAllWorkFormats();
        setWorkFormats(
          workFormatsData.map((format) => ({
            value: format.id,
            label: translateWorkFormat(format.name),
            originalName: format.name, // Сохраняем оригинальное название для отправки на сервер
          })),
        );
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
        setUsers([]);
        setGroups([]);
        setCompanies([]);
        setAccessLevels([]);
        setWorkFormats([]);
        setSections([]);
        setGroupUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      const fetchUserData = async () => {
        try {
          const userData = await getUserById(selectedUser.value);

          if (!userData) {
            throw new Error("Данные пользователя не найдены");
          }

          const user = userData;
          setUserData(user);

          // Извлекаем последние значения из истории для дат эффективности
          // Для зарплаты ищем дату конкретного значения, а не последнюю запись
          const salaryDate = getSalaryDateFromHistory(
            user.salary_history,
            user.salary,
          );
          const latestStatusEntry = getLatestValueFromHistory(
            user.user_status_history,
            "status",
          );
          const latestGroupEntry = getLatestValueFromHistory(
            user.user_group_history,
            "group",
          );
          const latestRoleEntry = getLatestValueFromHistory(
            user.user_primary_role_history,
            "primary_role",
          );
          const latestSectionEntry = getLatestValueFromHistory(
            user.primary_section_history,
            "primary_section",
          );
          const latestTeamLeaderEntry = getLatestValueFromHistory(
            user.primary_team_leader_history,
            "primary_team_leader",
          );
          const latestGradeEntry = getLatestValueFromHistory(
            user.user_grade_history,
            "grade",
          );
          const latestWorkFormatEntry = getLatestValueFromHistory(
            user.work_format_history,
            "work_format",
          );
          const latestWorkStartTimeEntry = getLatestValueFromHistory(
            user.work_start_time_history,
            "work_start_time",
          );
          const latestWorkHoursPerDayEntry = getLatestValueFromHistory(
            user.work_hours_per_day_history,
            "work_hours_per_day",
          );

          const loadedFormData = applyPendingEffectiveDates(
            user.id,
            user,
            {
              username: user.username,
              email: user.email,
              lastName: user.surname,
              firstName: user.name,
              middleName: user.patronymic,
              isFired: resolveIsFiredFromUser(user),
              hireDate: user.hire_date,
              accessLevel: user.access_levels.map((level) => level.id),
              salary: user.salary?.toString() || "",
              image: user.image,
              birthDate: user.birth_date,
              phone: user.phone_number || "",
              hobbies: user.hobby,
              companyId: user.company_id,
              groups: user.departments || [],
              roles:
                user.departments && Array.isArray(user.departments)
                  ? user.departments.flatMap((dept) =>
                      dept.groups
                        ? dept.groups.flatMap((group) => group.roles || [])
                        : [],
                    )
                  : [],
              teamLeaders: user.team_leaders
                ? user.team_leaders.map((leader) => ({
                    id: leader.id,
                    label: `${leader.surname} ${leader.name}`,
                    value: leader.id,
                  }))
                : [],
              groupLeaders: user.group_leaders || [],
              departmentHeads: user.department_heads || [],
              grade: user.hr_data?.grade || "",
              workFormat: user.hr_data?.work_format?.id || 1,
              workHoursPerDay: user.hr_data?.work_hours_per_day || 8,
              workStartTime: user.hr_data?.work_start_time || "09:00:00",
              firedDate: resolveFiredDate(user),
              salaryEffectiveDate: resolveSalaryEffectiveDate(user),
              companyIdEffectiveDate: resolveCompanyEffectiveDateForForm(user),
              departmentEffectiveDate:
                resolveDepartmentEffectiveDateForForm(user),
              groupEffectiveDate: latestGroupEntry?.effective_date || "",
              roleEffectiveDate: latestRoleEntry?.effective_date || "",
              sectionEffectiveDate: latestSectionEntry?.effective_date || "",
              teamLeadersEffectiveDate:
                latestTeamLeaderEntry?.effective_date || "",
              primaryTeamLeaderIdEffectiveDate:
                latestTeamLeaderEntry?.effective_date || "",
              primaryGroupIdEffectiveDate:
                latestGroupEntry?.effective_date || "",
              primarySectionIdEffectiveDate:
                latestSectionEntry?.effective_date || "",
              primaryRoleIdEffectiveDate: latestRoleEntry?.effective_date || "",
              gradeEffectiveDate: latestGradeEntry?.effective_date || "",
              workFormatEffectiveDate:
                latestWorkFormatEntry?.effective_date || "",
              workHoursPerDayEffectiveDate:
                resolveWorkHoursPerDayEffectiveDate(user),
              workStartTimeEffectiveDate:
                latestWorkStartTimeEntry?.effective_date || "",
            },
          );
          setFormData(loadedFormData);
          setFinansistContragent(
            contragentMappingToFormState(
              getUserFinansistContragentMapping(user),
            ),
          );

          const finalPositions = buildPositionsFromUser(user, companies);

          setPositions(finalPositions);
          // Валидируем поля дат после установки positions
          setTimeout(() => validateDateFields(finalPositions), 100);

          // Загружаем роли и секции для первой позиции, если есть группа
          if (finalPositions.length > 0 && finalPositions[0].group) {
            const firstPosition = finalPositions[0];
            if (firstPosition.group && firstPosition.group.value) {
              try {
                // Загружаем роли для группы
                const rolesData = await getAllRoles(
                  firstPosition.group.value,
                  true,
                );

                // Загружаем секции для группы
                const sectionsData = await getAllSections(
                  firstPosition.group.value,
                  true,
                );
                const sectionsOptions = sectionsData.map((section) => ({
                  value: section.id,
                  label: section.name,
                }));
                setSections(sectionsOptions);

                // Обновляем позицию с информацией о ролях и секциях
                const updatedPositions = [...finalPositions];
                updatedPositions[0].group = {
                  ...updatedPositions[0].group,
                  roles: rolesData,
                  sections: sectionsOptions,
                };
                setPositions(updatedPositions);
              } catch (error) {
                console.error("Ошибка при загрузке ролей и секций:", error);
              }
            }
          }
        } catch (error) {
          console.error("Ошибка при загрузке данных пользователя:", error);
        }
      };
      fetchUserData();
    }
  }, [selectedUser, companies, validateDateFields]);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      minHeight: "42px",
      width: "100%",
      boxSizing: "border-box",
    }),
    menu: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      marginTop: "5px",
      width: "100%",
      zIndex: 2,
    }),
    container: (provided) => ({
      ...provided,
      width: "100%",
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
    }),
    multiValue: (provided, state) => ({
      ...provided,
      borderRadius: "5px",
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      padding: "2px 8px",
    }),
    input: (provided, state) => ({
      ...provided,
      margin: "0px",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };

  const handleUserChange = async (selectedOption) => {
    const userId = selectedOption.value;
    setSelectedUser(selectedOption);
    setLoading(true);

    try {
      const response = await getUserById(userId);
      console.log("Ответ от API:", response);

      if (!response) {
        throw new Error("Данные пользователя не найдены");
      }

      const user = response;

      // Устанавливаем данные пользователя один раз с правильной структурой
      const processedUser = {
        ...user,
        team_leaders: user.team_leaders || [],
        group_leaders: user.group_leaders || [],
        department_heads: user.department_heads || [],
      };
      setUserData(processedUser);

      const loadedPositions = buildPositionsFromUser(user, companies);
      setPositions(loadedPositions);
      setTimeout(() => validateDateFields(loadedPositions), 100);

      // Загружаем роли и секции для первой позиции, если есть группа
      if (loadedPositions.length > 0 && loadedPositions[0].group) {
        const firstPosition = loadedPositions[0];
        if (firstPosition.group?.value) {
          try {
            const rolesData = await getAllRoles(
              firstPosition.group.value,
              true,
            );
            const sectionsData = await getAllSections(
              firstPosition.group.value,
              true,
            );
            const sectionsOptions = sectionsData.map((section) => ({
              value: section.id,
              label: section.name,
            }));
            setSections(sectionsOptions);

            const updatedPositions = [...loadedPositions];
            updatedPositions[0].group = {
              ...updatedPositions[0].group,
              roles: rolesData,
              sections: sectionsOptions,
            };
            setPositions(updatedPositions);
          } catch (error) {
            console.error("Ошибка при загрузке ролей и секций:", error);
          }
        }
      }

      // Извлекаем последние значения из истории для дат эффективности
      // Для зарплаты ищем дату конкретного значения, а не последнюю запись
      const salaryDate = getSalaryDateFromHistory(
        user.salary_history,
        user.salary,
      );
      const latestStatusEntry = getLatestValueFromHistory(
        user.user_status_history,
        "status",
      );
      const latestGroupEntry = getLatestValueFromHistory(
        user.user_group_history,
        "group",
      );
      const latestRoleEntry = getLatestValueFromHistory(
        user.user_primary_role_history,
        "primary_role",
      );
      const latestSectionEntry = getLatestValueFromHistory(
        user.primary_section_history,
        "primary_section",
      );
      const latestTeamLeaderEntry = getLatestValueFromHistory(
        user.primary_team_leader_history,
        "primary_team_leader",
      );
      const latestGradeEntry = getLatestValueFromHistory(
        user.user_grade_history,
        "grade",
      );
      const latestWorkFormatEntry = getLatestValueFromHistory(
        user.work_format_history,
        "work_format",
      );
      const latestWorkStartTimeEntry = getLatestValueFromHistory(
        user.work_start_time_history,
        "work_start_time",
      );
      const latestWorkHoursPerDayEntry = getLatestValueFromHistory(
        user.work_hours_per_day_history,
        "work_hours_per_day",
      );

      // Обновляем initialData и formData с правильными названиями полей
      const formattedData = {
        username: user.username || "",
        email: user.email || "",
        lastName: user.surname || "",
        firstName: user.name || "",
        middleName: user.patronymic || "",
        isFired: resolveIsFiredFromUser(user),
        hireDate: formatDate(user.hire_date) || "",
        accessLevel: (user.access_levels || []).map((level) => level.id),
        accessLevelEffectiveDate: (() => {
          const latestEntry = getLatestValueFromHistory(
            user.access_levels_history,
            "access_level",
          );
          console.log("Access levels history:", user.access_levels_history);
          console.log("Latest access level entry:", latestEntry);
          return latestEntry?.effective_date || "";
        })(),
        salary: user.salary?.toString() || "",
        image: user.image || "",
        birthDate: user.birth_date,
        phone: user.phone_number || "",
        hobbies: user.hobby || "",
        companyId: user.company_id || null,
        groups: user.groups || [],
        roles:
          user.groups && Array.isArray(user.groups)
            ? user.groups.flatMap((group) => group.roles || [])
            : [],
        teamLeaders: user.team_leaders || [],
        groupLeaders: user.group_leaders || [],
        departmentHeads: user.department_heads || [],
        // HR данные - используем текущие значения из hr_data
        grade: user.hr_data?.grade || "",
        workFormat: user.hr_data?.work_format?.id || 1,
        workHoursPerDay: user.hr_data?.work_hours_per_day || 8,
        workStartTime: user.hr_data?.work_start_time || "09:00:00",
        // Даты эффективности из истории
        firedDate: resolveFiredDate(user),
        salaryEffectiveDate: resolveSalaryEffectiveDate(user),
        companyIdEffectiveDate: resolveCompanyEffectiveDateForForm(user),
        departmentEffectiveDate: resolveDepartmentEffectiveDateForForm(user),
        groupEffectiveDate: latestGroupEntry?.effective_date || "",
        roleEffectiveDate: latestRoleEntry?.effective_date || "",
        sectionEffectiveDate: latestSectionEntry?.effective_date || "",
        teamLeadersEffectiveDate: latestTeamLeaderEntry?.effective_date || "",
        primaryTeamLeaderIdEffectiveDate:
          latestTeamLeaderEntry?.effective_date || "",
        primaryGroupIdEffectiveDate: latestGroupEntry?.effective_date || "",
        primarySectionIdEffectiveDate: latestSectionEntry?.effective_date || "",
        primaryRoleIdEffectiveDate: latestRoleEntry?.effective_date || "",
        gradeEffectiveDate: latestGradeEntry?.effective_date || "",
        workFormatEffectiveDate: latestWorkFormatEntry?.effective_date || "",
        workHoursPerDayEffectiveDate: resolveWorkHoursPerDayEffectiveDate(user),
        workStartTimeEffectiveDate:
          latestWorkStartTimeEntry?.effective_date || "",
      };

      const formWithPending = applyPendingEffectiveDates(
        user.id,
        user,
        formattedData,
      );
      setInitialFormData(formWithPending);
      setFormData(formWithPending);

      setLoading(false);
    } catch (error) {
      console.error("Ошибка при загрузке данных пользователя:", error);
      setInputErrors([error.message]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;

    setFormData((prevData) => {
      const newData = {
        ...prevData,
        [name]:
          name === "phone" ? value.replace(/[^0-9+()-]/g, "") : value || "",
      };
      console.log("New form data:", newData); // Добавляем лог
      return newData;
    });

    // Принудительно обновляем компонент для пересчета isFormValid
    setFormValidationTrigger((prev) => prev + 1);

    // Валидация для email и username
    if (name === "email" || name === "username") {
      const pattern = /^[A-Za-z0-9@._-]+$/;
      if (!pattern.test(value)) {
        setInputErrors((prev) => [
          ...prev.filter((e) => !e.includes(`Поле ${name}`)),
          `Поле ${name} должно содержать только латиницу`,
        ]);
      } else {
        setInputErrors((prev) =>
          prev.filter((e) => !e.includes(`Поле ${name}`)),
        );
      }
    }
  }, []);

  const handleDepartmentChange = async (index, selectedOption) => {
    const newPositions = [...positions];
    newPositions[index].group = selectedOption;
    newPositions[index].role = null;
    newPositions[index].section = null;

    // Загружаем роли и секции для выбранной группы
    if (selectedOption && selectedOption.value) {
      try {
        // Загружаем роли
        const rolesData = await getAllRoles(selectedOption.value, true);
        newPositions[index].group = {
          ...selectedOption,
          roles: rolesData,
        };

        // Загружаем секции для выбранной группы
        const sectionsData = await getAllSections(selectedOption.value, true);
        const sectionsOptions = sectionsData.map((section) => ({
          value: section.id,
          label: section.name,
        }));
        setSections(sectionsOptions);

        // Обновляем группу с информацией о секциях
        newPositions[index].group = {
          ...selectedOption,
          roles: rolesData,
          sections: sectionsOptions,
        };

        // Загружаем пользователей отдела
        const usersData = await getUsersSummary({
          groupId: selectedOption.value,
        });
        setGroupUsers(
          usersData.users.map((user) => ({
            value: user.id,
            label: `${user.surname} ${user.name}`,
          })),
        );
      } catch (error) {
        console.error("Ошибка при загрузке данных для группы:", error);
        newPositions[index].group = {
          ...selectedOption,
          roles: [],
        };
        setSections([]);
        setGroupUsers([]);
      }
    } else {
      setSections([]);
      setGroupUsers([]);
    }

    setPositions(newPositions);
  };

  const handleRoleChange = (index, selectedOption) => {
    const newPositions = [...positions];
    newPositions[index].role = selectedOption;
    setPositions(newPositions);
  };

  const handleSectionChange = (index, selectedOption) => {
    const newPositions = [...positions];
    newPositions[index].section = selectedOption;
    setPositions(newPositions);
  };

  const handleTeamLeaderChange = (index, selectedOption) => {
    const newPositions = [...positions];
    newPositions[index].teamLeader = selectedOption;
    setPositions(newPositions);
  };

  const addPosition = () => {
    setPositions([...positions, { id: Date.now(), ...EMPTY_POSITION }]);
  };

  const removePosition = (id) => {
    if (positions.length > 1) {
      setPositions(positions.filter((position) => position.id !== id));
    }
  };

  const handlePositionEffectiveDateChange = (index, field, date) => {
    const dateValue = date ? date.toISOString().split("T")[0] : "";
    const newPositions = [...positions];
    newPositions[index][field] = dateValue;
    setPositions(newPositions);

    if (index === 0) {
      if (field === "companyEffectiveDate") {
        setFormData((prev) => ({
          ...prev,
          companyIdEffectiveDate: dateValue,
        }));
      }
      if (field === "departmentEffectiveDate") {
        setFormData((prev) => ({
          ...prev,
          departmentEffectiveDate: dateValue,
        }));
      }
    }

    validateDateFields(newPositions);
    setFormValidationTrigger((prev) => prev + 1);
  };

  const handlePositionChange = (id, field, value) => {
    setPositions(
      positions.map((position) =>
        position.id === id ? { ...position, [field]: value } : position,
      ),
    );
  };

  const accessLevelOptions = accessLevels.map((level) => ({
    value: level.id,
    label: level.name,
  }));

  const handleAccessLevelChange = (selectedOptions) => {
    setFormData((prevData) => ({
      ...prevData,
      accessLevel: selectedOptions.map((option) => option.value),
    }));
  };

  const firedStatusOptions = [
    { value: true, label: "Уволен" },
    { value: false, label: "Не уволен" },
  ];

  const handleFiredStatusChange = (selectedOption) => {
    setFormData((prevData) => ({
      ...prevData,
      isFired: selectedOption.value,
    }));
  };

  const handleCompanyChange = (index, selectedOption) => {
    const newPositions = [...positions];
    newPositions[index].company = selectedOption;
    newPositions[index].group = null;
    newPositions[index].role = null;
    setPositions(newPositions);

    // Обновляем formData.companyId
    setFormData((prevData) => ({
      ...prevData,
      companyId: selectedOption ? selectedOption.value : null,
    }));
  };

  const validateUserData = (data) => {
    const requiredFields = [
      "username",
      "email",
      "surname",
      "name",
      "access_levels",
      "salary",
      "company_id",
      "group_ids",
      "role_ids",
      "image",
    ];

    // Удалим hire_date из списка обязательных полей

    const missingFields = requiredFields.filter((field) => {
      if (field === "image") {
        return (
          data[field] === undefined ||
          data[field] === null ||
          data[field] === ""
        );
      }
      return data[field] === undefined || data[field] === null;
    });

    if (missingFields.length > 0) {
      throw new Error(
        `Отсутствуют обязательные поля: ${missingFields.join(", ")}`,
      );
    }

    if (data.is_fired === undefined) {
      throw new Error("Поле 'is_fired' должно быть определено");
    }

    if (data.group_ids.length === 0) {
      throw new Error("Необходимо выбрать хотя бы одну группу");
    }

    if (data.role_ids.length === 0) {
      throw new Error("Необходимо выбрать хотя бы одну роль");
    }
  };

  // Предположим, у нас есть функция для получения ID по имени
  const getIdByName = (name, userList) => {
    const user = userList.find((u) => u.name === name);
    return user ? user.id : null;
  };

  const convertToIds = (arr) => {
    // Предположим, у вас есть объект, сопоставляющий имена с ID
    const nameToIdMap = {
      Андрей: 1,
      // Добавьте другие соответствия имен и ID
    };

    return arr
      .map((name) => nameToIdMap[name])
      .filter((id) => id !== undefined);
  };

  const formatErrorMessage = (error) => {
    const fieldMap = {
      team_leaders: "Руководители команд",
      group_leaders: "Руководители групп",
      department_heads: "Руководители отделов",
      username: "Логин",
      email: "Email",
      surname: "Фамилия",
      name: "Имя",
      access_levels: "Уровни доступа",
      salary: "Оклад",
      company_id: "Компания",
      group_ids: "Отделы",
      role_ids: "Должности",
    };

    if (error.type === "missing") {
      const fieldName = fieldMap[error.loc[1]] || error.loc[1];
      return `Поле "${fieldName}" обязательно для заполнения`;
    }
    if (error.type === "value_error") {
      const fieldName = fieldMap[error.loc[1]] || error.loc[1];
      return `Неверное значение в поле "${fieldName}": ${error.msg}`;
    }
    return error.msg;
  };

  /**
   * Для режима частичного редактирования (АДМС): только изменённые поля из сокращённой формы.
   * Поля: фото (отдельный запрос), увольнение, имя, фамилия, отчество, email, дата рождения, логин, хобби, телефон, контрагент Finansist.
   */
  const buildFinansistContragentPayloadFromForm = (currentFormData) =>
    buildFinansistContragentMappingPayload({
      finansistContragent: finansistContragent.finansistContragent,
      matchedFio: formatMatchedFio({
        surname: currentFormData.lastName,
        name: currentFormData.firstName,
        patronymic: currentFormData.middleName,
      }),
    });

  const getPartialEditChangedFields = (currentData, originalData) => {
    const changes = {};
    const normBirth = (d) =>
      !d ? "" : d.toString().split("T")[0].substring(0, 10);

    // Увольнение и дату всегда отдаём вместе при изменении любого из них
    const firedChanged =
      normalizeIsFired(currentData.isFired) !==
      resolveIsFiredFromUser(originalData);
    const normDate = (d) =>
      !d ? "" : d.toString().split("T")[0].substring(0, 10);
    const firedDateChanged =
      normDate(currentData.firedDate) !== normDate(resolveFiredDate(originalData));
    if (firedChanged || firedDateChanged) {
      changes.is_fired = normalizeIsFired(currentData.isFired);
      changes.is_fired_date =
        currentData.firedDate?.trim() ||
        new Date().toISOString().split("T")[0];
    }
    if (
      (currentData.firstName || "").trim() !== (originalData.name || "").trim()
    ) {
      changes.name = (currentData.firstName || "").trim();
    }
    if (
      (currentData.lastName || "").trim() !==
      (originalData.surname || "").trim()
    ) {
      changes.surname = (currentData.lastName || "").trim();
    }
    if (
      (currentData.middleName || "").trim() !==
      (originalData.patronymic || "").trim()
    ) {
      changes.patronymic = (currentData.middleName || "").trim();
    }
    if (
      (currentData.email || "").trim() !== (originalData.email || "").trim()
    ) {
      changes.email = (currentData.email || "").trim();
    }
    if (
      normBirth(currentData.birthDate) !== normBirth(originalData.birth_date)
    ) {
      changes.birth_date = currentData.birthDate
        ? normBirth(currentData.birthDate)
        : originalData.birth_date;
    }
    if (
      (currentData.username || "").trim() !==
      (originalData.username || "").trim()
    ) {
      changes.username = (currentData.username || "").trim();
    }
    if (
      (currentData.hobbies || "").trim() !== (originalData.hobby || "").trim()
    ) {
      changes.hobby = (currentData.hobbies || "").trim() || "";
    }
    const currPhone = (currentData.phone || "").replace(/\D/g, "");
    const origPhone = (originalData.phone_number || "").replace(/\D/g, "");
    if (currPhone !== origPhone) {
      changes.phone_number = currentData.phone || "";
    }

    if (isFinansistContragentMappingChanged(finansistContragent, originalData)) {
      const finansistMapping =
        buildFinansistContragentPayloadFromForm(currentData);
      if (finansistMapping) {
        changes.finansist_contragent_mapping = finansistMapping;
      } else {
        const originalMapping = getUserFinansistContragentMapping(originalData);
        if (originalMapping?.finansist_contragent_id) {
          changes.finansist_contragent_mapping = {
            finansist_contragent_id: Number(
              originalMapping.finansist_contragent_id,
            ),
            finansist_contragent_name:
              originalMapping.finansist_contragent_name || "",
            matched_fio: originalMapping.matched_fio || "",
            is_active: false,
          };
        }
      }
    }

    return changes;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setValidationErrors([]);
    setInputErrors([]);
    setImageErrors([]);

    try {
      if (!userData) {
        throw new Error("Данные пользователя не найдены");
      }

      // Режим частичного редактирования (АДМС): отправляем только изменённые поля
      if (isAdms && !fullEditMode) {
        const partialPayload = getPartialEditChangedFields(formData, userData);
        const hasImageChange = !!formData.imageFile;

        if (Object.keys(partialPayload).length === 0 && !hasImageChange) {
          setValidationErrors(["Нет изменений для сохранения"]);
          setLoading(false);
          return;
        }

        await updateUser(userData.id, partialPayload);

        if (formData.imageFile) {
          try {
            await updateUserImage(userData.id, formData.imageFile);
          } catch (imageError) {
            setImageErrors([
              imageError.message || "Ошибка при обновлении аватара",
            ]);
          }
        }

        const updatedUserResponse = await getUserById(userData.id);
        if (updatedUserResponse) {
          setUserData(updatedUserResponse);
          setFormData((prev) => {
            const u = updatedUserResponse;
            return {
              ...prev,
              username: u.username,
              email: u.email,
              lastName: u.surname,
              firstName: u.name,
              middleName: u.patronymic || "",
              isFired: resolveIsFiredFromUser(u),
              birthDate: u.birth_date || "",
              phone: u.phone_number || "",
              hobbies: u.hobby || "",
              imageFile: null,
              firedDate: resolveFiredDate(u),
            };
          });
          setFinansistContragent(
            contragentMappingToFormState(
              getUserFinansistContragentMapping(updatedUserResponse),
            ),
          );
        }
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 2000);
        setLoading(false);
        return;
      }

      console.log("Current form data before submit:", formData);
      console.log("Positions data:", positions);

      let updatedUserData = cleanUserUpdatePayload(
        buildUserUpdatePayload({
          formData,
          userData,
          positions,
          options: {
            includeOrg: !isFinanceOnlyEditor,
            includeSalary: true,
            includeWorkSchedule: true,
          },
        }),
      );

      if (isFinansistContragentMappingChanged(finansistContragent, userData)) {
        const finansistMapping = buildFinansistContragentPayloadFromForm(formData);
        if (finansistMapping) {
          updatedUserData.finansist_contragent_mapping = finansistMapping;
        } else {
          const originalMapping = getUserFinansistContragentMapping(userData);
          if (originalMapping?.finansist_contragent_id) {
            updatedUserData.finansist_contragent_mapping = {
              finansist_contragent_id: Number(
                originalMapping.finansist_contragent_id,
              ),
              finansist_contragent_name:
                originalMapping.finansist_contragent_name || "",
              matched_fio: originalMapping.matched_fio || "",
              is_active: false,
            };
          }
        }
      }

      console.log("Updated user data before sending:", updatedUserData);

      // ВАЖНО: Проверяем, что секция принадлежит выбранной группе
      // Если есть секция, но нет группы, или секция не принадлежит группе - убираем секцию
      if (updatedUserData.section && updatedUserData.group) {
        const selectedGroupId = updatedUserData.group[0];
        const selectedSectionId = updatedUserData.section[0];

        // Проверяем, что секция принадлежит выбранной группе
        // Используем данные из positions, где хранится информация о секциях группы
        const positionWithGroup = positions.find(
          (p) => p.group && p.group.value === selectedGroupId,
        );
        if (
          positionWithGroup &&
          positionWithGroup.group &&
          positionWithGroup.group.sections
        ) {
          const validSectionIds = positionWithGroup.group.sections.map(
            (s) => s.value,
          );
          if (!validSectionIds.includes(selectedSectionId)) {
            console.warn(
              "Секция не принадлежит выбранной группе, убираем секцию",
            );
            delete updatedUserData.section;
            delete updatedUserData.section_effective_date;
            delete updatedUserData.primary_section_id;
            delete updatedUserData.primary_section_id_effective_date;
          }
        }
      }

      const sentPayload = { ...updatedUserData };

      console.log("Данные для отправки на сервер:", updatedUserData);
      console.log(
        "Поле salary_effective_date:",
        updatedUserData.salary_effective_date,
      );
      console.log("Поле is_fired:", updatedUserData.is_fired);
      console.log("Поле is_fired_date:", updatedUserData.is_fired_date);
      console.log("Поле work_start_time:", updatedUserData.work_start_time);
      console.log(
        "Поле work_start_time_effective_date:",
        updatedUserData.work_start_time_effective_date,
      );
      console.log(
        "Поле work_hours_per_day:",
        updatedUserData.work_hours_per_day,
      );
      console.log(
        "Поле work_hours_per_day_effective_date:",
        updatedUserData.work_hours_per_day_effective_date,
      );

      // Дополнительная отладка для понимания что отправляется
      console.log("=== ОТЛАДКА ПОЛЕЙ ===");
      console.log(
        "is_fired в payload:",
        updatedUserData.hasOwnProperty("is_fired"),
      );
      console.log(
        "is_fired_date в payload:",
        updatedUserData.hasOwnProperty("is_fired_date"),
      );
      console.log(
        "work_start_time в payload:",
        updatedUserData.hasOwnProperty("work_start_time"),
      );
      console.log(
        "work_start_time_effective_date в payload:",
        updatedUserData.hasOwnProperty("work_start_time_effective_date"),
      );
      console.log(
        "work_hours_per_day в payload:",
        updatedUserData.hasOwnProperty("work_hours_per_day"),
      );
      console.log(
        "work_hours_per_day_effective_date в payload:",
        updatedUserData.hasOwnProperty("work_hours_per_day_effective_date"),
      );

      const result = await updateUser(userData.id, updatedUserData);
      savePendingEffectiveDates(userData.id, sentPayload);
      console.log("Пользователь успешно обновлен:", result);

      // Если был выбран новый файл изображения, обновляем аватар
      if (formData.imageFile) {
        try {
          console.log("Обновляем аватар пользователя...");
          await updateUserImage(userData.id, formData.imageFile);
          console.log("Аватар успешно обновлен");
        } catch (imageError) {
          console.error("Ошибка при обновлении аватара:", imageError);
          // Добавляем ошибку аватара в отдельное состояние
          setImageErrors([
            imageError.message || "Ошибка при обновлении аватара",
          ]);
        }
      }

      // После успешного обновления, обновляем данные пользователя
      // Получаем полные данные пользователя, включая историю
      const updatedUserResponse = await getUserById(userData.id);
      if (updatedUserResponse) {
        const updatedUser = updatedUserResponse;

        // Логируем данные для отладки
        console.log(
          "Полные данные пользователя после сохранения:",
          updatedUser,
        );
        console.log("История компаний:", updatedUser.company_history);
        setUserData(updatedUser);

        // Извлекаем последние значения из истории для обновленных данных
        // Для бухгалтера используем только что отправленные даты, а не из истории
        const salaryDate = isFinanceOnlyEditor
          ? updatedUserData.salary_effective_date
          : getSalaryDateFromHistory(
              updatedUser.salary_history,
              updatedUser.salary,
            );

        const latestGroupEntry = getLatestValueFromHistory(
          updatedUser.user_group_history,
          "group",
        );
        const latestRoleEntry = getLatestValueFromHistory(
          updatedUser.user_primary_role_history,
          "primary_role",
        );
        const latestSectionEntry = getLatestValueFromHistory(
          updatedUser.primary_section_history,
          "primary_section",
        );
        const latestTeamLeaderEntry = getLatestValueFromHistory(
          updatedUser.primary_team_leader_history,
          "primary_team_leader",
        );
        const latestGradeEntry = getLatestValueFromHistory(
          updatedUser.user_grade_history,
          "grade",
        );
        const latestWorkFormatEntry = getLatestValueFromHistory(
          updatedUser.work_format_history,
          "work_format",
        );
        const latestWorkStartTimeEntry = getLatestValueFromHistory(
          updatedUser.work_start_time_history,
          "work_start_time",
        );
        const latestWorkHoursPerDayEntry = getLatestValueFromHistory(
          updatedUser.work_hours_per_day_history,
          "work_hours_per_day",
        );
        const latestCompanyEntry = getLatestValueFromHistory(
          updatedUser.company_history,
          "company",
        );

        // Обновляем форму новыми данными
        const newFormData = {
          username: updatedUser.username || "",
          email: updatedUser.email || "",
          lastName: updatedUser.surname || "",
          firstName: updatedUser.name || "",
          middleName: updatedUser.patronymic || "",
          isFired: resolveIsFiredFromUser(updatedUser),
          hireDate: formatDate(updatedUser.hire_date) || "",
          accessLevel: (updatedUser.access_levels || []).map(
            (level) => level.id,
          ),
          accessLevelEffectiveDate:
            getLatestValueFromHistory(
              updatedUser.access_levels_history,
              "access_level",
            )?.effective_date || "",
          salary: updatedUser.salary?.toString() || "",
          image: updatedUser.image || "",
          birthDate: formatDate(updatedUser.birth_date) || "",
          phone: updatedUser.phone_number || "",
          hobbies: updatedUser.hobby || "",
          companyId: updatedUser.company_id || "",
          groups: updatedUser.groups || [],
          roles:
            updatedUser.groups && Array.isArray(updatedUser.groups)
              ? updatedUser.groups.flatMap((group) => group.roles || [])
              : [],
          teamLeaders: updatedUser.team_leaders || [],
          groupLeaders: updatedUser.group_leaders || [],
          departmentHeads: updatedUser.department_heads || [],
          // HR данные - используем последние значения из истории или текущие значения
          grade: updatedUser.latest_grade || updatedUser.hr_data?.grade || "",
          workFormat:
            updatedUser.latest_work_format?.id ||
            updatedUser.hr_data?.work_format?.id ||
            1,
          workHoursPerDay:
            updatedUser.latest_work_hours_per_day ||
            updatedUser.hr_data?.work_hours_per_day ||
            8,
          workStartTime:
            updatedUser.latest_work_start_time ||
            updatedUser.hr_data?.work_start_time ||
            "09:00:00",
          firedDate: resolveFiredDate(updatedUser),
          salaryEffectiveDate: resolveSalaryEffectiveDate(updatedUser),
          companyIdEffectiveDate:
            resolveCompanyEffectiveDateForForm(updatedUser),
          departmentEffectiveDate:
            resolveDepartmentEffectiveDateForForm(updatedUser),
          groupEffectiveDate: latestGroupEntry?.effective_date || "",
          roleEffectiveDate: latestRoleEntry?.effective_date || "",
          sectionEffectiveDate: latestSectionEntry?.effective_date || "",
          teamLeadersEffectiveDate: latestTeamLeaderEntry?.effective_date || "",
          primaryTeamLeaderIdEffectiveDate:
            latestTeamLeaderEntry?.effective_date || "",
          primaryGroupIdEffectiveDate: latestGroupEntry?.effective_date || "",
          primarySectionIdEffectiveDate:
            latestSectionEntry?.effective_date || "",
          primaryRoleIdEffectiveDate: latestRoleEntry?.effective_date || "",
          gradeEffectiveDate: latestGradeEntry?.effective_date || "",
          workFormatEffectiveDate: latestWorkFormatEntry?.effective_date || "",
          workHoursPerDayEffectiveDate:
            resolveWorkHoursPerDayEffectiveDate(updatedUser),
          workStartTimeEffectiveDate:
            latestWorkStartTimeEntry?.effective_date || "",
        };

        const mergedFormData = applyPendingEffectiveDates(
          userData.id,
          updatedUser,
          mergeSentEffectiveDates(newFormData, sentPayload),
        );
        setFormData(mergedFormData);
        setInitialFormData(mergedFormData);
        setFinansistContragent(
          contragentMappingToFormState(
            getUserFinansistContragentMapping(updatedUser),
          ),
        );

        const reloadedPositions = buildPositionsFromUser(updatedUser, companies);
        setPositions(reloadedPositions);

        // Загружаем роли и секции для первой позиции, если есть группа
        if (reloadedPositions.length > 0 && reloadedPositions[0].group) {
          const firstPosition = reloadedPositions[0];
          if (firstPosition.group && firstPosition.group.value) {
            try {
              // Загружаем роли для группы
              const rolesData = await getAllRoles(
                firstPosition.group.value,
                true,
              );

              // Загружаем секции для группы
              const sectionsData = await getAllSections(
                firstPosition.group.value,
                true,
              );
              const sectionsOptions = sectionsData.map((section) => ({
                value: section.id,
                label: section.name,
              }));
              setSections(sectionsOptions);

              // Обновляем позицию с информацией о ролях и секциях
              const updatedPositions = [...reloadedPositions];
              updatedPositions[0].group = {
                ...updatedPositions[0].group,
                roles: rolesData,
                sections: sectionsOptions,
              };
              setPositions(updatedPositions);
            } catch (error) {
              console.error("Ошибка при загрузке ролей и секций:", error);
            }
          }
        }
      }

      setShowSuccessPopup(true);

      // Сбрасываем страницу на изначальное состояние после успешного обновления
      setTimeout(() => {
        setShowSuccessPopup(false);
        // Сбрасываем выбранного пользователя
        setSelectedUser(null);
        // Сбрасываем данные пользователя
        setUserData(null);
        // Сбрасываем форму
        setFormData({
          accessLevel: [],
          accessLevelEffectiveDate: "",
          isFired: false,
          salary: "",
          firstName: "",
          lastName: "",
          middleName: "",
          birthDate: "",
          email: "",
          username: "",
          hobbies: "",
          phone: "",
          hireDate: "",
          companyId: null,
          groups: [],
          roles: [],
          teamLeaders: [],
          groupLeaders: [],
          departmentHeads: [],
        });
        // Сбрасываем позиции
        setPositions([{ id: Date.now(), ...EMPTY_POSITION }]);
        // Сбрасываем ошибки
        setInputErrors([]);
        setValidationErrors([]);
        setImageErrors([]);
        setDateFieldErrors({});
        setFinansistContragent(emptyFinansistContragentFormState());
      }, 3000);
    } catch (error) {
      console.error("Ошибка при обновлении пользователя:", error);

      // Обработка ошибок от API
      let errorMessage = "Произошла ошибка при обновлении пользователя";

      if (error.message) {
        // Если ошибка содержит сообщение, используем его
        errorMessage = error.message;
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.detail
      ) {
        // Обработка ошибок валидации от сервера
        if (Array.isArray(error.response.data.detail)) {
          const formattedErrors =
            error.response.data.detail.map(formatErrorMessage);
          setValidationErrors(formattedErrors);
          return; // Выходим, так как уже установили ошибки
        } else {
          errorMessage = error.response.data.detail;
        }
      }

      // Устанавливаем ошибку для отображения пользователю
      setValidationErrors([errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    // Сохраняем выбранного пользователя
    const currentSelectedUser = selectedUser;

    // Сбрасываем все поля формы
    setFormData({
      accessLevel: [],
      accessLevelEffectiveDate: "",
      isFired: false,
      salary: "",
      firstName: "",
      lastName: "",
      middleName: "",
      birthDate: "",
      email: "",
      username: "",
      hobbies: "",
      phone: "",
      hireDate: "",
      companyId: null,
      groups: [],
      roles: [],
      teamLeaders: [],
      groupLeaders: [],
      departmentHeads: [],
    });
    setFinansistContragent(emptyFinansistContragentFormState());

    // Сбрасываем позиции
    setPositions([
      {
        id: Date.now(),
        company: null,
        group: null,
        role: null,
        grade: "",
        section: "",
        teamLeader: null,
        // Отдельные даты эффективности для каждого поля
        companyEffectiveDate: "",
        groupEffectiveDate: "",
        roleEffectiveDate: "",
        sectionEffectiveDate: "",
        teamLeaderEffectiveDate: "",
      },
    ]);

    // Сбрасываем ошибки ввода
    setInputErrors([]);
    setImageErrors([]);
    setDateFieldErrors({});

    // Сбрасываем данные пользователя, но сохраняем выбранного пользователя
    setUserData(null);
    setSelectedUser(currentSelectedUser);

    // Загружаем данные пользователя заново
    if (currentSelectedUser) {
      try {
        const userData = await getUserById(currentSelectedUser.value);
        if (userData) {
          setUserData(userData);
        }
      } catch (error) {
        console.error("Ошибка при загрузке данных пользователя:", error);
      }
    }
  };

  const isFormValid = () => {
    // Только ФИН (без ИК): урезанная форма и свои обязательные даты
    if (isFinanceOnlyEditor) {
      return (
        formData.salaryEffectiveDate &&
        formData.salaryEffectiveDate.trim() !== "" &&
        formData.firedDate &&
        formData.firedDate.trim() !== "" &&
        formData.workFormatEffectiveDate &&
        formData.workFormatEffectiveDate.trim() !== "" &&
        formData.workHoursPerDayEffectiveDate &&
        formData.workHoursPerDayEffectiveDate.trim() !== "" &&
        formData.workStartTimeEffectiveDate &&
        formData.workStartTimeEffectiveDate.trim() !== ""
      );
    }

    // Для остальных уровней проверяем обязательные поля
    if (!formData.birthDate || !formData.hireDate) {
      return false;
    }

    // Для остальных уровней проверяем дополнительные обязательные даты
    // Проверяем основные даты из formData
    const basicRequiredDates = [
      "firedDate",
      "salaryEffectiveDate",
      "workFormatEffectiveDate",
      "workStartTimeEffectiveDate",
    ];

    const basicDatesFilled = basicRequiredDates.every(
      (field) => formData[field] && formData[field].trim() !== "",
    );

    // Проверяем все даты позиций (отдельные даты для каждого поля)
    const positionDatesFilled =
      positions.length > 0 &&
      positions[0].companyEffectiveDate &&
      positions[0].companyEffectiveDate.trim() !== "" &&
      positions[0].groupEffectiveDate &&
      positions[0].groupEffectiveDate.trim() !== "" &&
      positions[0].roleEffectiveDate &&
      positions[0].roleEffectiveDate.trim() !== "" &&
      positions[0].sectionEffectiveDate &&
      positions[0].sectionEffectiveDate.trim() !== "" &&
      positions[0].teamLeaderEffectiveDate &&
      positions[0].teamLeaderEffectiveDate.trim() !== "";

    return basicDatesFilled && positionDatesFilled;
  };

  // Добавляем обработчик для загрузки изображения
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        imageFile: file,
      }));
    }
  };

  if (loading) {
    return <Preloader />;
  }

  const userOptions = users.map((user) => ({
    value: user.id,
    label: `${user.name} ${user.surname}`,
  }));

  return (
    <div className="registration">
      <form className="registration__form" onSubmit={handleSubmit}>
        <ul className="registration__inputs">
          <li className="registration__inputs-container registration__inputs-container--full-width">
            <label className="registration__label">Выберите сотрудника</label>
            <Select
              options={userOptions}
              onChange={handleUserChange}
              value={selectedUser}
              placeholder="Выберите сотрудника"
              classNamePrefix="select"
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderRadius: "10px",
                  border: "1px solid #E0E0E0",
                  padding: "5px",
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  fontWeight: 300,
                  lineHeight: "19.12px",
                  minHeight: "42px",
                  width: "100%",
                  boxSizing: "border-box",
                }),
                menu: (provided, state) => ({
                  ...provided,
                  borderRadius: "10px",
                  marginTop: "5px",
                  width: "100%",
                  zIndex: 2,
                }),
                container: (provided) => ({
                  ...provided,
                  width: "100%",
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  padding: "2px 8px",
                }),
                input: (provided) => ({
                  ...provided,
                  margin: "0px",
                }),
                indicatorSeparator: () => ({
                  display: "none",
                }),
                placeholder: (provided) => ({
                  ...provided,
                  marginTop: "0",
                  marginBottom: "0",
                }),
              }}
            />
          </li>

          {/* Отображение ошибок валидации в верхней части */}
          {validationErrors.length > 0 && (
            <li className="registration__inputs-container registration__inputs-container--full-width">
              <div className="validation-errors validation-errors-top">
                <div className="validation-errors-header">
                  <span className="validation-errors-title">
                    Ошибки при обновлении:
                  </span>
                  <button
                    type="button"
                    className="validation-errors-close"
                    onClick={() => setValidationErrors([])}
                    aria-label="Закрыть ошибки"
                  >
                    ×
                  </button>
                </div>
                {validationErrors.map((error, index) => (
                  <div key={index} className="validation-error">
                    {error}
                  </div>
                ))}
              </div>
            </li>
          )}

          {/* Отображение ошибок аватара */}
          {imageErrors.length > 0 && (
            <li className="registration__inputs-container registration__inputs-container--full-width">
              <div className="validation-errors validation-errors-top">
                <div className="validation-errors-header">
                  <span className="validation-errors-title">
                    Ошибки при обновлении аватара:
                  </span>
                  <button
                    type="button"
                    className="validation-errors-close"
                    onClick={() => setImageErrors([])}
                    aria-label="Закрыть ошибки аватара"
                  >
                    ×
                  </button>
                </div>
                {imageErrors.map((error, index) => (
                  <div key={index} className="validation-error">
                    {error}
                  </div>
                ))}
              </div>
            </li>
          )}

          {/* Тумблер режима редактирования только для АДМС */}
          {userData && isAdms && (
            <li className="registration__inputs-container registration__inputs-container--full-width registration__edit-mode-toggle">
              <div className="registration__toggle-row">
                <span className="registration__toggle-label">
                  Частичное редактирование
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={fullEditMode}
                  aria-label={
                    fullEditMode
                      ? "Полное редактирование включено"
                      : "Частичное редактирование включено"
                  }
                  className={`registration__mode-switch ${fullEditMode ? "registration__mode-switch--full" : ""}`}
                  onClick={() => setFullEditMode((prev) => !prev)}
                >
                  <span className="registration__mode-switch-slider" />
                </button>
                <span className="registration__toggle-label">
                  Полное редактирование
                </span>
              </div>
            </li>
          )}

          {/* Фото должно быть первым после выбора пользователя */}
          {userData && (
            <>
              <li className="registration__inputs-container registration__inputs-container--full-width">
                <div className="registration__photo-container">
                  <img
                    src={
                      formData.imageFile
                        ? URL.createObjectURL(formData.imageFile)
                        : userData.image || EmployeePhoto
                    }
                    alt="Фото профиля"
                    className="registration__current-photo"
                  />
                  {!isFinanceOnlyEditor && (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="registration__file-input"
                        id="photo-upload-top"
                      />
                      <label
                        htmlFor="photo-upload-top"
                        className="registration__file-label"
                      >
                        Изменить фото
                      </label>
                    </>
                  )}
                </div>
              </li>

              {/* Раздел 1: Личные данные */}
              <li className="registration__section-header registration__section-header--full-width">
                <h3>Личные данные</h3>
              </li>

              {isAdms && !fullEditMode ? (
                <>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Увольнение*</label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.user_status_history}
                        fieldName="is_fired"
                      >
                        <Select
                          options={firedStatusOptions}
                          value={firedStatusOptions.find(
                            (option) => option.value === formData.isFired,
                          )}
                          onChange={handleFiredStatusChange}
                          classNamePrefix="select"
                          placeholder="Выберите статус увольнения"
                          noOptionsMessage={() => "Нет доступных опций"}
                          styles={customStyles}
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.firedDate
                            ? new Date(formData.firedDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "firedDate",
                              value: date.toISOString().split("T")[0],
                            },
                          })
                        }
                        maxDate={new Date()}
                      />
                    </div>
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Имя</label>
                    <input
                      className="registration__input"
                      name="firstName"
                      value={formData.firstName || ""}
                      onChange={handleInputChange}
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Фамилия</label>
                    <input
                      className="registration__input"
                      name="lastName"
                      value={formData.lastName || ""}
                      onChange={handleInputChange}
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Отчество</label>
                    <input
                      className="registration__input"
                      name="middleName"
                      value={formData.middleName || ""}
                      onChange={handleInputChange}
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Email</label>
                    <input
                      className="registration__input"
                      name="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={handleInputChange}
                      placeholder="example@company.com"
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Дата рождения</label>
                    <Calendar
                      value={
                        formData.birthDate ? new Date(formData.birthDate) : null
                      }
                      onChange={(date) =>
                        handleInputChange({
                          target: {
                            name: "birthDate",
                            value: date.toISOString().split("T")[0],
                          },
                        })
                      }
                      maxDate={new Date()}
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Логин</label>
                    <input
                      className="registration__input"
                      name="username"
                      value={formData.username || ""}
                      onChange={handleInputChange}
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Хобби</label>
                    <input
                      className="registration__input"
                      name="hobbies"
                      value={formData.hobbies || ""}
                      onChange={handleInputChange}
                      placeholder="нет данных"
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Телефон</label>
                    <InputWithRef
                      className="registration__input"
                      name="phone"
                      value={formData.phone || ""}
                      onAccept={(value) =>
                        handleInputChange({ target: { name: "phone", value } })
                      }
                      ref={phoneInputRef}
                      placeholder="Необязательно"
                    />
                  </li>
                  <FinansistContragentSelect
                    value={finansistContragent}
                    onChange={setFinansistContragent}
                  />
                  <div className="registration__buttons">
                    <button
                      type="submit"
                      className="newreport__button button-send button-active"
                    >
                      Сохранить изменения
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="newreport__button button-reset"
                    >
                      Сбросить
                    </button>
                  </div>
                </>
              ) : isFinanceOnlyEditor ? (
                <>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Оклад*</label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.salary_history}
                        fieldName="salary_amount"
                      >
                        <input
                          className="registration__input"
                          name="salary"
                          type="number"
                          value={formData.salary || ""}
                          onChange={handleInputChange}
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.salaryEffectiveDate
                            ? new Date(formData.salaryEffectiveDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "salaryEffectiveDate",
                              value: date.toISOString().split("T")[0],
                            },
                          })
                        }
                        maxDate={new Date()}
                      />
                    </div>
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Увольнение*</label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.user_status_history}
                        fieldName="is_fired"
                      >
                        <Select
                          options={firedStatusOptions}
                          value={firedStatusOptions.find(
                            (option) => option.value === formData.isFired,
                          )}
                          onChange={handleFiredStatusChange}
                          classNamePrefix="select"
                          placeholder="Выберите статус увольнения"
                          noOptionsMessage={() => "Нет доступных опций"}
                          styles={customStyles}
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.firedDate
                            ? new Date(formData.firedDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "firedDate",
                              value: date.toISOString().split("T")[0],
                            },
                          })
                        }
                        maxDate={new Date()}
                      />
                    </div>
                  </li>
                  <li className="registration__section-header registration__section-header--full-width">
                    <h3>График и формат работы</h3>
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Формат работы</label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.work_format_history}
                        fieldName="work_format"
                      >
                        <Select
                          options={workFormats}
                          styles={customStyles}
                          value={
                            formData.workFormat
                              ? workFormats.find(
                                  (format) =>
                                    format.value === formData.workFormat,
                                ) || {
                                  value: formData.workFormat,
                                  label: formData.workFormat,
                                }
                              : null
                          }
                          onChange={(selectedOption) =>
                            handleInputChange({
                              target: {
                                name: "workFormat",
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            })
                          }
                          placeholder="Выберите формат работы"
                          noOptionsMessage={() => "Форматы работы не найдены"}
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.workFormatEffectiveDate
                            ? new Date(formData.workFormatEffectiveDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "workFormatEffectiveDate",
                              value: date
                                ? date.toISOString().split("T")[0]
                                : "",
                            },
                          })
                        }
                        placeholder="Выберите дату"
                        maxDate={new Date()}
                      />
                    </div>
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">
                      Рабочие часы в день
                    </label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.work_hours_per_day_history}
                        fieldName="work_hours_per_day"
                      >
                        <Select
                          options={[
                            { value: 1, label: "1 час" },
                            { value: 2, label: "2 часа" },
                            { value: 3, label: "3 часа" },
                            { value: 4, label: "4 часа" },
                            { value: 5, label: "5 часов" },
                            { value: 6, label: "6 часов" },
                            { value: 7, label: "7 часов" },
                            { value: 8, label: "8 часов" },
                          ]}
                          styles={customStyles}
                          value={
                            formData.workHoursPerDay
                              ? {
                                  value: formData.workHoursPerDay,
                                  label: `${formData.workHoursPerDay} часов`,
                                }
                              : null
                          }
                          onChange={(selectedOption) =>
                            handleInputChange({
                              target: {
                                name: "workHoursPerDay",
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            })
                          }
                          placeholder="Выберите рабочие часы в день"
                          noOptionsMessage={() => "Варианты не найдены"}
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.workHoursPerDayEffectiveDate
                            ? new Date(formData.workHoursPerDayEffectiveDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "workHoursPerDayEffectiveDate",
                              value: date
                                ? date.toISOString().split("T")[0]
                                : "",
                            },
                          })
                        }
                        placeholder="Выберите дату"
                        maxDate={new Date()}
                      />
                    </div>
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">
                      Начало дня по графику
                    </label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.work_start_time_history}
                        fieldName="work_start_time"
                      >
                        <Select
                          options={[
                            { value: "08:30:00", label: "08:30" },
                            { value: "09:00:00", label: "09:00" },
                            { value: "09:30:00", label: "09:30" },
                            { value: "13:30:00", label: "13:30" },
                          ]}
                          styles={customStyles}
                          value={
                            formData.workStartTime
                              ? {
                                  value: formData.workStartTime,
                                  label: formData.workStartTime.substring(0, 5),
                                }
                              : null
                          }
                          onChange={(selectedOption) =>
                            handleInputChange({
                              target: {
                                name: "workStartTime",
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            })
                          }
                          placeholder="Выберите начало рабочего дня по графику"
                          noOptionsMessage={() => "Время не найдено"}
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.workStartTimeEffectiveDate
                            ? new Date(formData.workStartTimeEffectiveDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "workStartTimeEffectiveDate",
                              value: date
                                ? date.toISOString().split("T")[0]
                                : "",
                            },
                          })
                        }
                        placeholder="Выберите дату"
                        maxDate={new Date()}
                      />
                    </div>
                  </li>
                  <FinansistContragentSelect
                    value={finansistContragent}
                    onChange={setFinansistContragent}
                  />
                  <div className="registration__buttons">
                    <button
                      type="submit"
                      className={`newreport__button button-send ${
                        !isFormValid() ? "button-disabled" : "button-active"
                      }`}
                      disabled={!isFormValid()}
                    >
                      Сохранить изменения
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="newreport__button button-reset"
                    >
                      Сбросить
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {!isHRInspector && (
                    <>
                      <li className="registration__inputs-container">
                        <label className="registration__label">
                          Уровень доступа*
                        </label>
                        <div className="registration__input-with-date">
                          <HistoryTooltip
                            history={userData?.access_levels_history}
                            fieldName="access_level"
                          >
                            <Select
                              isMulti
                              options={accessLevelOptions}
                              value={accessLevelOptions.filter((option) =>
                                formData.accessLevel.includes(option.value),
                              )}
                              onChange={handleAccessLevelChange}
                              classNamePrefix="select"
                              placeholder="Выберите уровень доступа"
                              noOptionsMessage={() => "Нет доступных опций"}
                              styles={customStyles}
                            />
                          </HistoryTooltip>
                          <Calendar
                            value={
                              formData.accessLevelEffectiveDate
                                ? new Date(formData.accessLevelEffectiveDate)
                                : null
                            }
                            onChange={(date) =>
                              handleInputChange({
                                target: {
                                  name: "accessLevelEffectiveDate",
                                  value: date.toISOString().split("T")[0],
                                },
                              })
                            }
                            maxDate={new Date()}
                          />
                        </div>
                      </li>
                    </>
                  )}
                  <li className="registration__inputs-container">
                    <label className="registration__label">Увольнение*</label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.user_status_history}
                        fieldName="is_fired"
                      >
                        <Select
                          options={firedStatusOptions}
                          value={firedStatusOptions.find(
                            (option) => option.value === formData.isFired,
                          )}
                          onChange={handleFiredStatusChange}
                          classNamePrefix="select"
                          placeholder="Выберите статус увольнения"
                          noOptionsMessage={() => "Нет доступных опций"}
                          styles={customStyles}
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.firedDate
                            ? new Date(formData.firedDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "firedDate",
                              value: date.toISOString().split("T")[0],
                            },
                          })
                        }
                        maxDate={new Date()}
                      />
                    </div>
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Имя*</label>
                    <input
                      className="registration__input"
                      name="firstName"
                      value={formData.firstName || ""}
                      onChange={handleInputChange}
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Фамилия*</label>
                    <input
                      className="registration__input"
                      name="lastName"
                      value={formData.lastName || ""}
                      onChange={handleInputChange}
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Отчество</label>
                    <input
                      className="registration__input"
                      name="middleName"
                      value={formData.middleName}
                      onChange={handleInputChange}
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Email</label>
                    <input
                      className="registration__input"
                      name="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={handleInputChange}
                      placeholder="example@company.com"
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">
                      Дата рождения*
                    </label>
                    <Calendar
                      value={
                        formData.birthDate ? new Date(formData.birthDate) : null
                      }
                      onChange={(date) =>
                        handleInputChange({
                          target: {
                            name: "birthDate",
                            value: date.toISOString().split("T")[0],
                          },
                        })
                      }
                      maxDate={new Date()}
                    />
                  </li>

                  {!isHRInspector && (
                    <li className="registration__inputs-container">
                      <label className="registration__label">Логин*</label>
                      <input
                        className="registration__input"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                      />
                    </li>
                  )}
                  <li className="registration__inputs-container">
                    <label className="registration__label">
                      Дата приема на работу
                    </label>
                    <Calendar
                      value={
                        formData.hireDate ? new Date(formData.hireDate) : null
                      }
                      onChange={(date) =>
                        handleInputChange({
                          target: {
                            name: "hireDate",
                            value: date.toISOString().split("T")[0],
                          },
                        })
                      }
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Хобби</label>
                    <input
                      className="registration__input"
                      name="hobbies"
                      value={formData.hobbies || ""}
                      onChange={handleInputChange}
                      placeholder="нет данных"
                    />
                  </li>
                  <li className="registration__inputs-container">
                    <label className="registration__label">Телефон</label>
                    <InputWithRef
                      className="registration__input"
                      name="phone"
                      value={formData.phone || ""}
                      onAccept={(value) =>
                        handleInputChange({ target: { name: "phone", value } })
                      }
                      ref={phoneInputRef}
                      placeholder="Необязательно"
                    />
                  </li>
                  <FinansistContragentSelect
                    value={finansistContragent}
                    onChange={setFinansistContragent}
                  />

                  {/* Раздел 2: Организация и должность */}
                  <li className="registration__section-header registration__section-header--full-width">
                    <h3>Организация и должность</h3>
                  </li>

                  {positions.map((position, index) => (
                    <div key={position.id} className="position-group">
                      {positions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePosition(position.id)}
                          className="remove-position-button"
                          aria-label="Удалить позицию"
                        >
                          &times;
                        </button>
                      )}
                      <li className="registration__inputs-container">
                        <label className="registration__label">Компания</label>
                        <Select
                          options={companies}
                          styles={customStyles}
                          value={position.company}
                          onChange={(selectedOption) =>
                            handleCompanyChange(index, selectedOption)
                          }
                          placeholder="Выберте компанию"
                          noOptionsMessage={() => "Компании не найдены"}
                        />
                      </li>
                      <li
                        className={`registration__inputs-container ${
                          dateFieldErrors[`${index}_companyEffectiveDate`]
                            ? "error"
                            : ""
                        }`}
                      >
                        <label className="registration__label">
                          Дата вступления в силу (Компания)
                        </label>
                        <HistoryTooltip
                          history={userData?.company_history}
                          fieldName="company"
                        >
                          <Calendar
                            value={
                              position.companyEffectiveDate
                                ? new Date(position.companyEffectiveDate)
                                : null
                            }
                            onChange={(date) =>
                              handlePositionEffectiveDateChange(
                                index,
                                "companyEffectiveDate",
                                date,
                              )
                            }
                            placeholder="Выберите дату"
                            maxDate={new Date()}
                          />
                        </HistoryTooltip>
                        {dateFieldErrors[`${index}_companyEffectiveDate`] && (
                          <span className="field-error-message">
                            {dateFieldErrors[`${index}_companyEffectiveDate`]}
                          </span>
                        )}
                      </li>
                      <li className="registration__inputs-container">
                        <label className="registration__label">Отдел</label>
                        <Select
                          options={groups}
                          styles={customStyles}
                          value={position.group}
                          onChange={(selectedOption) =>
                            handleDepartmentChange(index, selectedOption)
                          }
                          placeholder="Выберите отдел"
                          noOptionsMessage={() => "Отделы не найдены"}
                          isDisabled={!position.company}
                        />
                      </li>
                      <li
                        className={`registration__inputs-container ${
                          dateFieldErrors[`${index}_groupEffectiveDate`]
                            ? "error"
                            : ""
                        }`}
                      >
                        <label className="registration__label">
                          Дата вступления в силу (Отдел)
                        </label>
                        <HistoryTooltip
                          history={userData?.user_group_history}
                          fieldName="group"
                        >
                          <Calendar
                            value={
                              position.groupEffectiveDate
                                ? new Date(position.groupEffectiveDate)
                                : null
                            }
                            onChange={(date) =>
                              handlePositionEffectiveDateChange(
                                index,
                                "groupEffectiveDate",
                                date,
                              )
                            }
                            placeholder="Выберите дату"
                            maxDate={new Date()}
                          />
                        </HistoryTooltip>
                        {dateFieldErrors[`${index}_groupEffectiveDate`] && (
                          <span className="field-error-message">
                            {dateFieldErrors[`${index}_groupEffectiveDate`]}
                          </span>
                        )}
                      </li>
                      <li className="registration__inputs-container">
                        <label className="registration__label">Должность</label>
                        <Select
                          options={
                            position.group && position.group.roles
                              ? position.group.roles
                                  .filter((role) => role.is_active)
                                  .map((role) => ({
                                    value: role.id,
                                    label: role.name,
                                  }))
                              : []
                          }
                          styles={customStyles}
                          value={position.role}
                          onChange={(selectedOption) =>
                            handleRoleChange(index, selectedOption)
                          }
                          placeholder={
                            position.group &&
                            position.group.roles &&
                            position.group.roles.filter(
                              (role) => role.is_active,
                            ).length > 0
                              ? "Выберите должность"
                              : "Нет должности для выбора"
                          }
                          noOptionsMessage={() => "Должности не найдены"}
                          isDisabled={
                            !position.group ||
                            !position.group.roles ||
                            position.group.roles.filter(
                              (role) => role.is_active,
                            ).length === 0
                          }
                        />
                      </li>
                      <li
                        className={`registration__inputs-container ${
                          dateFieldErrors[`${index}_roleEffectiveDate`]
                            ? "error"
                            : ""
                        }`}
                      >
                        <label className="registration__label">
                          Дата вступления в силу (Должность)
                        </label>
                        <HistoryTooltip
                          history={userData?.user_primary_role_history}
                          fieldName="primary_role"
                        >
                          <Calendar
                            value={
                              position.roleEffectiveDate
                                ? new Date(position.roleEffectiveDate)
                                : null
                            }
                            onChange={(date) =>
                              handlePositionEffectiveDateChange(
                                index,
                                "roleEffectiveDate",
                                date,
                              )
                            }
                            placeholder="Выберите дату"
                            maxDate={new Date()}
                          />
                        </HistoryTooltip>
                        {dateFieldErrors[`${index}_roleEffectiveDate`] && (
                          <span className="field-error-message">
                            {dateFieldErrors[`${index}_roleEffectiveDate`]}
                          </span>
                        )}
                      </li>
                      <li className="registration__inputs-container">
                        <label className="registration__label">Секция</label>
                        <Select
                          options={sections}
                          styles={customStyles}
                          value={position.section}
                          onChange={(selectedOption) =>
                            handleSectionChange(index, selectedOption)
                          }
                          placeholder="Выберите секцию"
                          noOptionsMessage={() => "Секции не найдены"}
                          isDisabled={!position.group}
                        />
                      </li>
                      <li
                        className={`registration__inputs-container ${
                          dateFieldErrors[`${index}_sectionEffectiveDate`]
                            ? "error"
                            : ""
                        }`}
                      >
                        <label className="registration__label">
                          Дата вступления в силу (Секция)
                        </label>
                        <HistoryTooltip
                          history={userData?.primary_section_history}
                          fieldName="primary_section"
                        >
                          <Calendar
                            value={
                              position.sectionEffectiveDate
                                ? new Date(position.sectionEffectiveDate)
                                : null
                            }
                            onChange={(date) =>
                              handlePositionEffectiveDateChange(
                                index,
                                "sectionEffectiveDate",
                                date,
                              )
                            }
                            placeholder="Выберите дату"
                            maxDate={new Date()}
                          />
                        </HistoryTooltip>
                        {dateFieldErrors[`${index}_sectionEffectiveDate`] && (
                          <span className="field-error-message">
                            {dateFieldErrors[`${index}_sectionEffectiveDate`]}
                          </span>
                        )}
                      </li>
                      <li className="registration__inputs-container">
                        <label className="registration__label">
                          Руководитель группы
                        </label>
                        <Select
                          options={groupUsers}
                          value={position.teamLeader}
                          onChange={(selectedOption) =>
                            handleTeamLeaderChange(index, selectedOption)
                          }
                          placeholder="Выберите руководителя группы"
                          classNamePrefix="select"
                          styles={customStyles}
                          isClearable
                        />
                      </li>
                      <li
                        className={`registration__inputs-container ${
                          dateFieldErrors[`${index}_teamLeaderEffectiveDate`]
                            ? "error"
                            : ""
                        }`}
                      >
                        <label className="registration__label">
                          Дата вступления в силу (Руководитель группы)
                        </label>
                        <HistoryTooltip
                          history={userData?.primary_team_leader_history}
                          fieldName="primary_team_leader"
                        >
                          <Calendar
                            value={
                              position.teamLeaderEffectiveDate
                                ? new Date(position.teamLeaderEffectiveDate)
                                : null
                            }
                            onChange={(date) =>
                              handlePositionEffectiveDateChange(
                                index,
                                "teamLeaderEffectiveDate",
                                date,
                              )
                            }
                            placeholder="Выберите дату"
                            maxDate={new Date()}
                          />
                        </HistoryTooltip>
                        {dateFieldErrors[
                          `${index}_teamLeaderEffectiveDate`
                        ] && (
                          <span className="field-error-message">
                            {
                              dateFieldErrors[
                                `${index}_teamLeaderEffectiveDate`
                              ]
                            }
                          </span>
                        )}
                      </li>
                    </div>
                  ))}

                  <div className="position-buttons">
                    <button
                      type="button"
                      onClick={addPosition}
                      className="newreport__button button-add button-active"
                    >
                      +
                    </button>
                  </div>

                  {inputErrors.length > 0 && (
                    <div className="error-messages">
                      {inputErrors.map((error, index) => (
                        <p key={index} className="error-message">
                          {error}
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Отображение ошибок валидации */}
                  {validationErrors.length > 0 && (
                    <li className="registration__inputs-container registration__inputs-container--full-width">
                      <div className="validation-errors">
                        <div className="validation-errors-header">
                          <span className="validation-errors-title">
                            Ошибки при обновлении:
                          </span>
                          <button
                            type="button"
                            className="validation-errors-close"
                            onClick={() => setValidationErrors([])}
                            aria-label="Закрыть ошибки"
                          >
                            ×
                          </button>
                        </div>
                        {validationErrors.map((error, index) => (
                          <div key={index} className="validation-error">
                            {error}
                          </div>
                        ))}
                      </div>
                    </li>
                  )}

                  {/* Общие поля для всех должностей */}
                  {/* Временно скрыто поле Грейдинг */}
                  {false && (
                    <li className="registration__inputs-container">
                      <label className="registration__label">Грейдинг*</label>
                      <div className="registration__input-with-date">
                        <input
                          className="registration__input"
                          name="grade"
                          type="text"
                          value={formData.grade || ""}
                          onChange={handleInputChange}
                          placeholder="Введите грейдинг"
                        />
                        <Calendar
                          value={
                            formData.gradeEffectiveDate
                              ? new Date(formData.gradeEffectiveDate)
                              : null
                          }
                          onChange={(date) =>
                            handleInputChange({
                              target: {
                                name: "gradeEffectiveDate",
                                value: date
                                  ? date.toISOString().split("T")[0]
                                  : "",
                              },
                            })
                          }
                          placeholder="Выберите дату"
                          maxDate={new Date()}
                        />
                      </div>
                    </li>
                  )}

                  <li className="registration__inputs-container">
                    <label className="registration__label">Зарплата*</label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.salary_history}
                        fieldName="salary_amount"
                      >
                        <input
                          className="registration__input"
                          name="salary"
                          type="number"
                          value={formData.salary || ""}
                          onChange={handleInputChange}
                          placeholder="Введите оклад"
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.salaryEffectiveDate
                            ? new Date(formData.salaryEffectiveDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "salaryEffectiveDate",
                              value: date
                                ? date.toISOString().split("T")[0]
                                : "",
                            },
                          })
                        }
                        placeholder="Выберите дату"
                        maxDate={new Date()}
                      />
                    </div>
                  </li>

                  {/* Раздел 3: График и формат работы */}
                  <li className="registration__section-header registration__section-header--full-width">
                    <h3>График и формат работы</h3>
                  </li>

                  <li className="registration__inputs-container">
                    <label className="registration__label">Формат работы</label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.work_format_history}
                        fieldName="work_format"
                      >
                        <Select
                          options={workFormats}
                          styles={customStyles}
                          value={
                            formData.workFormat
                              ? workFormats.find(
                                  (format) =>
                                    format.value === formData.workFormat,
                                ) || {
                                  value: formData.workFormat,
                                  label: formData.workFormat,
                                }
                              : null
                          }
                          onChange={(selectedOption) =>
                            handleInputChange({
                              target: {
                                name: "workFormat",
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            })
                          }
                          placeholder="Выберите формат работы"
                          noOptionsMessage={() => "Форматы работы не найдены"}
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.workFormatEffectiveDate
                            ? new Date(formData.workFormatEffectiveDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "workFormatEffectiveDate",
                              value: date
                                ? date.toISOString().split("T")[0]
                                : "",
                            },
                          })
                        }
                        placeholder="Выберите дату"
                        maxDate={new Date()}
                      />
                    </div>
                  </li>

                  <li className="registration__inputs-container">
                    <label className="registration__label">
                      Рабочие часы в день
                    </label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.work_hours_per_day_history}
                        fieldName="work_hours_per_day"
                      >
                        <Select
                          options={[
                            { value: 1, label: "1 час" },
                            { value: 2, label: "2 часа" },
                            { value: 3, label: "3 часа" },
                            { value: 4, label: "4 часа" },
                            { value: 5, label: "5 часов" },
                            { value: 6, label: "6 часов" },
                            { value: 7, label: "7 часов" },
                            { value: 8, label: "8 часов" },
                          ]}
                          styles={customStyles}
                          value={
                            formData.workHoursPerDay
                              ? {
                                  value: formData.workHoursPerDay,
                                  label: `${formData.workHoursPerDay} часов`,
                                }
                              : null
                          }
                          onChange={(selectedOption) =>
                            handleInputChange({
                              target: {
                                name: "workHoursPerDay",
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            })
                          }
                          placeholder="Выберите рабочие часы в день"
                          noOptionsMessage={() => "Варианты не найдены"}
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.workHoursPerDayEffectiveDate
                            ? new Date(formData.workHoursPerDayEffectiveDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "workHoursPerDayEffectiveDate",
                              value: date
                                ? date.toISOString().split("T")[0]
                                : "",
                            },
                          })
                        }
                        placeholder="Выберите дату"
                        maxDate={new Date()}
                      />
                    </div>
                  </li>

                  <li className="registration__inputs-container">
                    <label className="registration__label">
                      Начало дня по графику
                    </label>
                    <div className="registration__input-with-date">
                      <HistoryTooltip
                        history={userData?.work_start_time_history}
                        fieldName="work_start_time"
                      >
                        <Select
                          options={[
                            { value: "08:30:00", label: "08:30" },
                            { value: "09:00:00", label: "09:00" },
                            { value: "09:30:00", label: "09:30" },
                            { value: "13:30:00", label: "13:30" },
                          ]}
                          styles={customStyles}
                          value={
                            formData.workStartTime
                              ? {
                                  value: formData.workStartTime,
                                  label: formData.workStartTime.substring(0, 5), // Показываем только часы:минуты
                                }
                              : null
                          }
                          onChange={(selectedOption) =>
                            handleInputChange({
                              target: {
                                name: "workStartTime",
                                value: selectedOption
                                  ? selectedOption.value
                                  : "",
                              },
                            })
                          }
                          placeholder="Выберите начало рабочего дня по графику"
                          noOptionsMessage={() => "Время не найдено"}
                        />
                      </HistoryTooltip>
                      <Calendar
                        value={
                          formData.workStartTimeEffectiveDate
                            ? new Date(formData.workStartTimeEffectiveDate)
                            : null
                        }
                        onChange={(date) =>
                          handleInputChange({
                            target: {
                              name: "workStartTimeEffectiveDate",
                              value: date
                                ? date.toISOString().split("T")[0]
                                : "",
                            },
                          })
                        }
                        placeholder="Выберите дату"
                        maxDate={new Date()}
                      />
                    </div>
                  </li>

                  <div className="registration__buttons">
                    <button
                      type="submit"
                      className={`newreport__button button-send ${
                        !isFormValid() ? "button-disabled" : "button-active"
                      }`}
                      disabled={!isFormValid()}
                    >
                      Сохранить изменения
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="newreport__button button-reset"
                    >
                      Сбросить
                    </button>
                  </div>

                  {/* Сообщения об обязательных полях */}
                  <div className="required-field-messages">
                    {!formData.birthDate && (
                      <span className="required-field-message">
                        Поле даты рождения обязательно
                      </span>
                    )}
                    {!formData.hireDate && (
                      <span className="required-field-message">
                        Поле даты приема на работу обязательно
                      </span>
                    )}
                    {!isFinanceOnlyEditor &&
                      (!formData.firedDate ||
                        formData.firedDate.trim() === "" ||
                        !formData.salaryEffectiveDate ||
                        formData.salaryEffectiveDate.trim() === "" ||
                        !formData.workFormatEffectiveDate ||
                        formData.workFormatEffectiveDate.trim() === "" ||
                        !formData.workStartTimeEffectiveDate ||
                        formData.workStartTimeEffectiveDate.trim() === "" ||
                        !positions[0]?.companyEffectiveDate ||
                        positions[0].companyEffectiveDate.trim() === "" ||
                        !positions[0]?.groupEffectiveDate ||
                        positions[0].groupEffectiveDate.trim() === "" ||
                        !positions[0]?.roleEffectiveDate ||
                        positions[0].roleEffectiveDate.trim() === "" ||
                        !positions[0]?.sectionEffectiveDate ||
                        positions[0].sectionEffectiveDate.trim() === "" ||
                        !positions[0]?.teamLeaderEffectiveDate ||
                        positions[0].teamLeaderEffectiveDate.trim() === "") && (
                        <span className="required-field-message">
                          Необходимо заполнить все даты
                        </span>
                      )}
                    {isFinanceOnlyEditor &&
                      (!formData.salaryEffectiveDate ||
                        formData.salaryEffectiveDate.trim() === "" ||
                        !formData.firedDate ||
                        formData.firedDate.trim() === "" ||
                        !formData.workFormatEffectiveDate ||
                        formData.workFormatEffectiveDate.trim() === "" ||
                        !formData.workHoursPerDayEffectiveDate ||
                        formData.workHoursPerDayEffectiveDate.trim() === "" ||
                        !formData.workStartTimeEffectiveDate ||
                        formData.workStartTimeEffectiveDate.trim() === "") && (
                        <span className="required-field-message">
                          Для ФИН обязательны даты: оклада, статуса, формата и
                          графика
                        </span>
                      )}
                  </div>
                </>
              )}
            </>
          )}

          {showSuccessPopup && (
            <div className="popup-overlay">
              <div className="popup">
                <div className="popup-icon"></div>
                <p className="popup-text">Пользователь успешно обновлен</p>
              </div>
            </div>
          )}
        </ul>
      </form>
    </div>
  );
}

export default UpdateUser;
